import React, { useContext, useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logout } from '../services/auth';
import SocketContext from '../context/socket';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getLatestNotifications, getUnreadNotificationsCount, updateNotification } from '../services/notifications';
import { onMutateSuccess, onQuerySuccess } from '../utils/queryResponse';
import { getSelf, updateProfile } from '../services/users';
import { isUserAdmin } from '../utils/roleHelpers';
import Avatar from './avatar';
import LanguageSwitcher from './languageSwitcher';

const NavigationBar = () => {

    const { t } = useTranslation();
    const socket = useContext(SocketContext);
    const [user, setUser] = useState()
    const [scrollOffset, setScrollOffset] = useState(0);

    const [notifications, setNotifications] = useState([])
    const [unreadNotifCount, setUnreadNotifCount] = useState()

    const queryClient = useQueryClient()

    const { data: notificationData } = useQuery(['notifications', 'latest'], () => getLatestNotifications(3))
    const { data: unreadNotifCountData } = useQuery(['notifications', 'unread-count'], () => getUnreadNotificationsCount(), {
        refetchInterval: 30 * 1000  // poll every 30s — fallback when socket is unavailable
    })
    const { data: userData } = useQuery(['profile'], getSelf)

    const handleLogout = () => {
        logout()
        window.location.reload();
    }

    const { mutate: mutateMarkAsRead } = useMutation(updateNotification, {
        onSuccess: onMutateSuccess(() => {
            queryClient.invalidateQueries(['notifications'])
        })
    })

    const { mutate: mutateLocale } = useMutation(updateProfile, {
        onSuccess: onMutateSuccess((res) => {
            if (res?.token) localStorage.setItem('cleaning-crm-token', res.token)
        })
    })

    const saveLanguageToProfile = (code) => {
        queryClient.setQueryData(['profile'], (old) => old ? { ...old, message: { ...old.message, locale: code } } : old)
        mutateLocale({ locale: code })
    }

    useEffect(() => {
        if (!user) return

        const companyId = user.activeCompany.id

        // Existing: late-job events from the external scheduler
        socket.on(`late-job-${companyId}`, () => {
            queryClient.invalidateQueries(['notifications'])
        })

        // Existing: delete-request events (admin only)
        isUserAdmin() && socket.on(`delete-job-approval-${companyId}`, () => {
            queryClient.invalidateQueries(['notifications'])
        })

        // New: covers all new business-event notifications emitted by the API
        socket.on(`job-notification-${companyId}`, () => {
            queryClient.invalidateQueries(['notifications'])
            queryClient.invalidateQueries(['overview-dashboard'])
            queryClient.invalidateQueries(['low-stock-count'])
        })

        return () => {
            socket.off(`late-job-${companyId}`)
            socket.off(`delete-job-approval-${companyId}`)
            socket.off(`job-notification-${companyId}`)
        }
    }, [socket, user, queryClient])

    useEffect(() => {
        onQuerySuccess(notificationData, (result) => {
            setNotifications(result)
        })
        onQuerySuccess(unreadNotifCountData, (result) => {
            setUnreadNotifCount(result)
        })
        onQuerySuccess(userData, (result) => {
            setUser(result)
        })
    }, [notificationData, unreadNotifCountData, userData])

    useEffect(() => {
        const onScroll = () => setScrollOffset(window.pageYOffset);
        window.removeEventListener('scroll', onScroll);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const getNotifMessage = (notif) => {
        const id = notif.objectId
        switch (notif.message) {
            case 'unpicked job':           return t('notifications.messages.unpickedJob', { id })
            case 'excessive pickup':       return t('notifications.messages.excessivePickup', { id })
            case 'not started cleaning':   return t('notifications.messages.notStartedCleaning', { id })
            case 'excessive cleaning':     return t('notifications.messages.excessiveCleaning', { id })
            case 'not started dispatch':   return t('notifications.messages.notStartedDispatch', { id })
            case 'excessive dispatch':     return t('notifications.messages.excessiveDispatch', { id })
            case 'invoice overdue':        return t('notifications.messages.invoiceOverdue', { id })
            case 'delete request':         return t('notifications.messages.deleteRequest', { id })
            case 'job assigned pickup':    return t('notifications.messages.jobAssignedPickup', { id })
            case 'job assigned cleaning':  return t('notifications.messages.jobAssignedCleaning', { id })
            case 'job assigned delivery':  return t('notifications.messages.jobAssignedDelivery', { id })
            case 'job cancelled':          return t('notifications.messages.jobCancelled', { id })
            case 'job completed':          return t('notifications.messages.jobCompleted', { id })
            case 'job paid':               return t('notifications.messages.jobPaid', { id })
            case 'reschedule needed':      return t('notifications.messages.rescheduleNeeded', { id })
            case 'job note added':         return t('notifications.messages.jobNoteAdded', { id: notif.jobId })
            case 'job delete approved':    return t('notifications.messages.jobDeleteApproved', { id })
            case 'reminder due':           return t('notifications.messages.reminderDue', { id })
            case 'client intake received': return t('notifications.messages.clientIntakeReceived')
            default:                       return notif.message?.startsWith('low stock: ')
                ? t('notifications.messages.lowStock', { name: notif.message.slice('low stock: '.length) })
                : notif.message
        }
    }

    const readNotif = notif => {
        mutateMarkAsRead({ id: notif.id, isRead: true })
    }

    const getNotifLink = (notif) => {
        if (notif.object === 'client') return 'clients?tab=pending'
        if (notif.object === 'material') return 'materials'
        if (notif.object === 'job' && notif.jobId) return `jobs?jobId=${notif.jobId}`
        if (notif.object === 'job') return `jobs?jobId=${notif.objectId}`
        return 'notifications'
    }

    return (
        <>
            <nav className="navbar sb-topbar sticky-top flex-md-nowrap justify-content-between">

                {/* Logo shows only on mobile (the sidebar that normally carries it is
                    hidden there); the flex-grow spacer keeps the action icons right-aligned. */}
                <div className="d-flex align-items-center flex-grow-1">
                    <Link to="/" className="sb-mlogo d-flex d-md-none align-items-center" title={t('nav.sorobyHome')}>
                        <img src={process.env.PUBLIC_URL + '/logo-soroby.svg'} alt="Soroby" />
                    </Link>
                </div>


                <ul className="navbar-nav">
                    <li>
                        <span className="sb-iconbtn sb-iconbtn--danger" role="button" tabIndex={0} title={t('nav.signOut')} onClick={() => handleLogout()}><i className="fa-solid fa-power-off"></i></span>
                    </li>

                    <li className="d-none d-md-flex align-items-center">
                        <Link to='/profile' className='sb-userchip' title={t('nav.yourProfile')}>
                            <Avatar className="sb-uava" size={30} name={user?.fullName || ''} src={user?.avatarUrl} />
                            <span className="sb-uname text-capitalize">{user?.fullName}</span>
                        </Link>
                    </li>

                    <li>
                        <Dropdown className="no-caret" drop="down-centered">
                            <Dropdown.Toggle variant="transparent" id="notification-drop">
                                <i className="fa-solid fa-bell"></i>
                                {notifications.length > 0 &&
                                    <sup><span className="badge bg-danger">{unreadNotifCount}</span></sup>
                                }
                            </Dropdown.Toggle>

                            <Dropdown.Menu style={{ boxShadow: '0 0 10px 2px rgba(0,30,0,0.15)', border: 0, padding: 0 }}>
                                {notifications.length > 0 && notifications.slice(0, 3).map((notif, index) =>
                                    <Dropdown.Item as="button" key={`notif-${index}`}
                                        style={{ borderBottom: '1px solid #d5e0d5' }}>
                                        <p style={{ margin: 0, height: '0.5rem' }}>&nbsp;</p>
                                        <Link onClick={() => readNotif(notif)} style={{ color: '#000' }} to={getNotifLink(notif)}>
                                            <i className="fa-solid fa-triangle-exclamation">&nbsp;</i>
                                            <small>{getNotifMessage(notif)}</small>
                                        </Link>
                                        <p style={{ margin: 0, height: '0.5rem' }}>&nbsp;</p>
                                    </Dropdown.Item>
                                )}
                                <Dropdown.Item as="button" className="p-0" key={`notif-view-all`}>
                                    <Link className="text-muted d-block px-3 py-2" to="notifications"><small>{t('nav.seeAllNotifications')}</small></Link>
                                </Dropdown.Item>
                            </Dropdown.Menu>

                        </Dropdown>

                    </li>

                    <li>
                        <LanguageSwitcher className="no-caret" onChange={saveLanguageToProfile} />
                    </li>

                </ul>
            </nav>
        </>
    )
}

export default NavigationBar;
